// @flow
import { delay } from 'redux-saga';
import { put, call, all, select, takeLatest } from 'redux-saga/effects';
import { selectToken } from 'ducks/SagaUtils';
import { type ActionType } from 'redux-actions';
// clients
import { selectClient, fetchClients, storeClients } from './clientsActions';
import { getClients } from './clientsApi';

// user Profles
import {
  fetchClientUserProfiles,
  storeClientUserProfiles,
  fetchClientExpertUserProfiles,
  storeClientExportUserProfiles,
} from './userProfileActions';
import { getUserProfiles, getExpertUserProfiles } from './userProfilesApi';
// alert Config
import {
  getModerationAlertConfig,
  createChannelAlertConfig,
  updateChannelAlertConfig,
} from './alertConfigApi';
import {
  fetchModerationAlertConfig,
  storeModerationAlertConfig,
  requestModerationAlertUpdate,
  receiveModerationAlertUpdate,
  clearModerationAlertUpdateStatus,
} from './alertConfigActions';
// client properties
import {
  fetchClientProperties,
  storeClientProperties,
} from './clientPropertiesActions';
import { getClientProperties } from './clientPropertiesApi';
// client catalogs
import { fetchClientCatalog, storeClientCatalog } from './catalogActions';
import { getClientCatalogs } from './catalogApi';
import { determineNextClient } from './clientsReducer';
import storage from './storage';
import { regenerateToken } from 'ducks/authActions';

export default function*(): Generator<*, *, *> {
  yield takeLatest(selectClient, onClientChange);
  yield takeLatest(fetchClients, clientsSaga);
  yield takeLatest(fetchClientUserProfiles, clientUserProfilesSaga);
  yield takeLatest(fetchClientExpertUserProfiles, clientExpertUserProfileSaga);
  yield takeLatest(fetchModerationAlertConfig, fetchModerationAlertConfigSaga);
  yield takeLatest(requestModerationAlertUpdate, setModerationAlertSaga);
  yield takeLatest(fetchClientProperties, clientPropertiesSaga);
  yield takeLatest(fetchClientCatalog, clientCatalogsSaga);
}
function* clientsSaga(
  action: ActionType<typeof fetchClients>
): Generator<*, *, *> {
  const token = yield select(selectToken);
  const currentClient = action.payload;

  try {
    const response = yield call(getClients, token);
    const { clients, currentClient: nextClient } = determineNextClient(
      response,
      currentClient
    );
    yield all([put(storeClients(clients)), put(selectClient(nextClient))]);
  } catch (err) {
    yield put(storeClients(err));
  }
}

function* onClientChange(
  action: ActionType<typeof selectClient>
): Generator<*, *, *> {
  const clientId = action.payload;

  if (clientId != null) {
    yield put(fetchClientUserProfiles(clientId));
    yield put(fetchClientExpertUserProfiles(clientId));
    yield put(fetchModerationAlertConfig(clientId));
    yield put(fetchClientProperties(clientId));
    yield put(fetchClientCatalog(clientId));
    yield put(regenerateToken(clientId));
    yield call(storage.setSelected, clientId);
  }
}

// user profiles
function* clientUserProfilesSaga(
  action: ActionType<typeof fetchClientUserProfiles>
): Generator<*, *, *> {
  const clientId = action.payload;
  const token = yield select(selectToken);

  try {
    const response = yield call(getUserProfiles, token, clientId);
    yield put(storeClientUserProfiles(response, clientId));
  } catch (err) {
    yield put(storeClientUserProfiles(err, clientId));
  }
}

function* clientExpertUserProfileSaga(
  action: ActionType<typeof fetchClientExpertUserProfiles>
): Generator<*, *, *> {
  const clientId = action.payload;
  const token = yield select(selectToken);

  try {
    const response = yield call(getExpertUserProfiles, token, clientId);
    yield put(storeClientExportUserProfiles(response, clientId));
  } catch (err) {
    yield put(storeClientExportUserProfiles(err, clientId));
  }
}

// client properties
function* clientPropertiesSaga(
  action: ActionType<typeof fetchClientProperties>
): Generator<*, *, *> {
  const clientId = action.payload;
  const token = yield select(selectToken);

  try {
    const response = yield call(getClientProperties, token, clientId);
    yield put(storeClientProperties(response, clientId));
  } catch (err) {
    yield put(storeClientProperties(err, clientId));
  }
}

// catalogs
function* clientCatalogsSaga(
  action: ActionType<typeof fetchClientCatalog>
): Generator<*, *, *> {
  const clientId = action.payload;
  const token = yield select(selectToken);

  try {
    const response = yield call(getClientCatalogs, token, clientId);
    yield put(storeClientCatalog(response, clientId));
  } catch (err) {
    yield put(storeClientCatalog(err, clientId));
  }
}

// alert config
function* fetchModerationAlertConfigSaga(
  action: ActionType<typeof fetchModerationAlertConfig>
): Generator<*, *, *> {
  const clientId = action.payload;
  const token = yield select(selectToken);

  try {
    const response = yield call(getModerationAlertConfig, token, clientId);
    yield put(storeModerationAlertConfig(response, clientId));
  } catch (err) {
    yield put(storeModerationAlertConfig(err, clientId));
  }
}

function* setModerationAlertSaga(
  action: ActionType<typeof requestModerationAlertUpdate>
): Generator<*, *, *> {
  const { payload: config, meta } = action;
  const method =
    'config_id' in config ? updateChannelAlertConfig : createChannelAlertConfig;
  const token = yield select(selectToken);

  try {
    const response = yield call(method, token, config);
    yield put(receiveModerationAlertUpdate(response, meta));
    yield call(delay, 2000);
    yield put(clearModerationAlertUpdateStatus(meta));
  } catch (err) {
    yield put(receiveModerationAlertUpdate(err, meta));
  }
}



// WEBPACK FOOTER //
// ./src/containers/client/saga.js